import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { withOpacity } from '@hrworks/design-system/functions';
import { mq } from '@hrworks/design-system/utilities';
import { Button } from '@hrworks/sui-core/Button';
import { CollapsibleMenuBaseItemStyles, ControlledCollapsibleMenuStyles } from '@hrworks/sui-core/CollapsibleMenu';
import { InputFieldStyles } from '@hrworks/sui-core/InputField';
import { Scroller as _Scroller } from '@hrworks/sui-core/Scroller';

import { SqwLayoutStyles } from '../';

const Container = styled.div(({ theme }) => {
  const { sidebar } = SqwLayoutStyles.componentConfig;

  const interactiveStyles = css({
    backgroundColor: withOpacity(theme.eclipseTier2Color.background.nav.selected, '50%'),
    color: theme.eclipseTier2Color.text.subtle,
  });

  const activeItemStyles = css({
    ...theme.eclipseTier2Typography.labelMdSemibold,
    backgroundColor: theme.eclipseTier2Color.background.nav.selected,
    color: theme.eclipseTier2Color.text.selected,

    [mq.supportsHover]: {
      ':hover': {
        backgroundColor: withOpacity(theme.eclipseTier2Color.background.nav.selected, '50%'),
      },
    },
  });

  return {
    //fix specificity after "FE-3767: unstyled CollapsibleMenu"
    '&&&': {
      backgroundColor: theme.eclipseTier2Color.background.nav.default,
      boxShadow: `1px 0 0 0 ${theme.eclipseTier2Color.border.nav.subtle}`,
      width: sidebar.width,
      zIndex: theme.marko.variables.zIndex.sidebar,
      display: 'flex',
      flexDirection: 'column',

      '&&': [
        CollapsibleMenuBaseItemStyles.generatePaddings({
          theme,
          iconFontSize: '1.5rem',
          iconGap: theme.eclipseTier1.spacing['16px'],
          horizontalItemPadding: `${sidebar.horizontalPadding}px`,
        }),
      ],

      [`${ControlledCollapsibleMenuStyles.sel.Container}`]: {
        padding: 0,
      },

      [`${CollapsibleMenuBaseItemStyles.sel.ItemContainer}`]: {
        transition: `background-color ${theme.marko.variables.animationDuration.normal}`,
        '&[data-expanded=true]': {
          backgroundColor: theme.eclipseTier2Color.background.nav.hovered,
        },
      },

      [`${CollapsibleMenuBaseItemStyles.sel.Item}:not(${CollapsibleMenuBaseItemStyles.sel.ItemContainer}  ${CollapsibleMenuBaseItemStyles.sel.ItemContainer} ${CollapsibleMenuBaseItemStyles.sel.Item} )`]:
        {
          ...theme.eclipseTier2Typography.labelMd,

          '&[data-active=true]': activeItemStyles,
        },

      [`${CollapsibleMenuBaseItemStyles.sel.ItemContainer} ${CollapsibleMenuBaseItemStyles.sel.ItemContainer} ${CollapsibleMenuBaseItemStyles.sel.Item}`]:
        {
          ...theme.eclipseTier2Typography.bodyMd,

          '&[data-active=true]': activeItemStyles,
        },

      [`${CollapsibleMenuBaseItemStyles.sel.IconContainer} svg`]: {
        width: 18,
      },

      [`${CollapsibleMenuBaseItemStyles.sel.Item}`]: [
        {
          borderRadius: 0,
          color: theme.eclipseTier2Color.text.subtle,
          minHeight: 50,
          height: '100%',

          ':focus-visible': {
            outlineColor: theme.eclipseTier2Color.border.focus,
          },

          [mq.supportsHover]: {
            ':hover': interactiveStyles,
          },

          ':active': interactiveStyles,
        },
      ],

      [`${InputFieldStyles.sel.Container}`]: {
        padding: `10px ${sidebar.horizontalPadding}px 30px`,
      },
    },
  };
});

const AdminButtonWrapper = styled.div({
  padding: '24px 8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const AdminButton = styled(Button)({
  borderRadius: 6,
  padding: '8px 12px',
  gap: 8,
});

const Scroller = styled(_Scroller)({
  overflowX: 'hidden',
});

const UpperSidebarChildrenContainer = styled.div({
  display: 'none',
  [mq.isSmallDevice]: {
    display: 'block',
  },
});

export const S = {
  Container,
  AdminButtonWrapper,
  AdminButton,
  Scroller,
  UpperSidebarChildrenContainer,
} as const;
